<!--
  Custom error screen [1]

  @link https://nuxtjs.org/docs/concepts/views/#error-page [1]
-->
<template>
  <div class="error-screen">

    <!-- Background -->
    <ui-wad class="overflow-hidden">
      <div class="error-screen__background" />
    </ui-wad>

    <!-- Content -->
    <div class="error-screen__content">
      <div
        v-if="error?.statusCode === 404"
        class="error-screen__jumbo-title"
      >
        Cette page n'existe pas

        <div class="font-family-heading font-weight-lighter font-size-24px mt-3">
          <ui-link
            :route="redirectionRoute"
            underline="always"
          >Cliquez ici</ui-link>
          pour être redirigé
        </div>
      </div>

      <!-- Erreur spécifique au localStorage -->
      <CommonErrorStorageError
        v-else-if="isStorageError"
        :error-message="error?.message"
        class="animate__animated animate__fadeIn d1"
      />

      <!-- Erreur générique -->
      <div v-else>
        <div
          :class="[
            'error-screen__title',
            'animate__animated animate__fadeIn d1'
          ]"
        >
          <div
            :class="[
              'error-screen__title__left',
               'animate__animated animate__fadeIn d2'
              ]"
            >
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path d="M34.9,-64.4C41.9,-56.4,41.9,-40.3,46.9,-28.2C51.9,-16.1,61.9,-8.1,67.4,3.2C73,14.4,74,28.9,66.8,37.1C59.5,45.3,44,47.4,31.5,53.9C19,60.4,9.5,71.4,-2.7,76.1C-14.9,80.7,-29.7,79,-37.9,70C-46.1,60.9,-47.7,44.7,-48.7,31.8C-49.6,18.9,-50,9.5,-54.2,-2.4C-58.4,-14.3,-66.4,-28.6,-62.4,-36.3C-58.5,-44,-42.6,-45.2,-30.2,-50C-17.8,-54.8,-8.9,-63.4,2.5,-67.8C14,-72.1,27.9,-72.3,34.9,-64.4Z" transform="translate(100 100)" />
            </svg>
            <ui-icon
              class="error-screen__title__left__icon"
              name="error_outline"
            />
          </div>
          <div class="error-screen__title__right">
            <p
              :class="[
                'error-screen__title__message',
                'mb-2',
              ]"
            >
              Une erreur s'est produite<br>
            </p>
            <div
              :class="[
                'error-screen__title__message',
                'mb-6 font-size-120',
                'animate__animated animate__fadeIn d6'
              ]"
            >
              <ul class="list-style-circle px-4">
                <li class="mb-1">Vous pouvez tenter de recharger la page</li>
                <li>Ou vous rediriger sur la page d'accueil en <ui-link underline="always" route="/">cliquant ici</ui-link></li>
              </ul>
            </div>
            <p
              :class="[
                'error-screen__title__message',
                'mb-2',
                'animate__animated animate__fadeIn d-2'
              ]"
            >
              Cause technique
            </p>
            <div
              :class="[
                'error-screen__title__payload',
                'animate__animated animate__fadeIn d-2'
              ]"
            >
              <ui-icon
                name="terminal"
              />
              <p>
                {{ technicalCauseMessage }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <transition
        type="animation"
        leave-active-class="animate__animated animate__fadeOutDown ease-in-out a3"
      >
        <ui-caveat
          v-if="showAutoRedirectionCaveat"
          :variant="UI.VARIANT.PRIMARY"
          :aside-variant="UI.VARIANT.PRIMARY"
          :size="UI.SIZE.SMALL"
          :class="[
            'error-screen__auto-redirection-caveat',
            'animate__animated animate__fadeInUp ease-in-out',
          ]"
        >
          <template #aside>
            <ui-wad class="d-flex justify-content-center align-items-center">
              <ui-spinner
                :type="UI.SPINNER_TYPE.BORDER"
                :size="UI.SIZE.MEDIUM"
              />
            </ui-wad>
          </template>
          <template #default>
            <p class="font-weight-light mb-2">
              Vous allez automatiquement être redirigé vers une page sûre dans quelques instants...
            </p>
            <ui-button
              :variant="UI.VARIANT.LIGHT"
              :size="UI.SIZE.SMALL"
              outline
              type="button"
              class="error-screen__cancel-redirection"
              @click="cancelAutoRedirection"
            >
              Annuler la redirection
            </ui-button>
          </template>
        </ui-caveat>
      </transition>
    </div>
  </div>
</template>

<style lang="sass" scoped>
$logo-text-size: 100px
$form-width-md: map-get($breakpoints, 'tablet')
$form-width-sm: 632px
$svg-size: 480px

.error-screen
  @extend %fit-waterline
  position: relative

  background: $wine-berry

  &__background
    @include inset
    position: absolute
    pointer-events: none
    background-repeat: repeat
    background-image: image('patterns/redox_01.png')
    filter: opacity(80%)
    mix-blend-mode: color-burn

  &__content
    margin: 20vh auto
    color: $white
    max-width: map-get($breakpoints, 'tablet')
    width: 100%
    padding: space-x(6)

  &__jumbo-title
    font-family: $font-family-heading-jumbo
    font-size: 64px

  &__title
    display: flex
    align-items: baseline

    &__left
      $icon-size: 60px
      position: relative
      margin-right: space-x(8)

      &__icon
        font-size: $icon-size

      svg
        opacity: 0.2
        left: space-x(-18)
        top: space-x(-18)
        position: absolute
        width: $svg-size
        height: $svg-size

        path
          fill: mix-alpha($danger, 80%)

    &__right
      flex-grow: 1

    &__message
      font-size: 140%
      margin-bottom: space-x(4)
      font-weight: $font-weight-light
      line-height: 1.2
      color: $white

    &__payload
      display: flex
      gap: space-x(2)
      font-size: 20px
      font-family: $font-family-monospace
      font-weight: $font-weight-bold
      padding: $card-spacer-y $card-spacer-x
      border-radius: $border-radius
      background: mix-alpha($white, 5%)

      .ui-icon
        position: relative
        font-size: 32px

      p
        align-self: flex-end

  &__auto-redirection-caveat
    position: absolute
    bottom: 50px
    left: 0
    right: 0
    margin: 0 auto
    width: 300px
    text-align: left

    /// Tachyons-like `a3` / `d-3`: explicit so delay applies on first mount (`d-2` = 2s).
    &.animate__animated.animate__fadeInUp
      animation-duration: 0.45s
      animation-delay: 2s

    :deep(.error-screen__cancel-redirection.btn-outline-light)
      color: $white
      border-color: mix-alpha($white, 45%)

      &:hover,
      &:focus-visible
        color: $white
        background-color: mix-alpha($white, 12%)
        border-color: mix-alpha($white, 70%)

</style>


<script setup lang="ts">
import type { NuxtError } from '#app'
import { routeForAnonymous } from '~/utils/lib/layout/redirection'
import { ERROR_MESSAGES } from '~/utils/lib/core/storage'
import CommonErrorStorageError from '~/components/common/error/StorageError.vue'

const AUTO_REDIRECTION_DELAY = 9000

const props = defineProps({
  // eslint-disable-next-line vue/require-default-prop
  error: Object as () => NuxtError
})

const redirectionRoute = routeForAnonymous()

/**
 * Detect if the error is related to a localStorage/sessionStorage problem
 * (for example when cookies are blocked or storage is disabled)
 */
const isStorageError = computed(() => {
  return props.error?.message === ERROR_MESSAGES.STORAGE_NOT_AVAILABLE
})

/**
 * After a delay, redirect to a safe route only for HTTP 404 (missing page).
 */
const hasAutoRedirection = computed(() => props.error?.statusCode === 404)

const autoRedirectionCancelled = ref(false)

const showAutoRedirectionCaveat = computed(
  () => hasAutoRedirection.value && !autoRedirectionCancelled.value,
)

function cancelAutoRedirection() {
  if (autoRedirectionTimeout.value != null) {
    clearTimeout(autoRedirectionTimeout.value)
    autoRedirectionTimeout.value = null
  }
  autoRedirectionCancelled.value = true
}

const technicalCauseMessage = computed(() => {
  if (!props.error) {
    return 'Erreur inconnue'
  }
  return props.error.message || props.error.statusMessage || 'Erreur inconnue'
})

const autoRedirectionTimeout = ref<NodeJS.Timeout | null>(null)

onMounted(() => {
  if (!hasAutoRedirection.value) {
    return
  }
  autoRedirectionTimeout.value = setTimeout(() => {
    navigateTo(redirectionRoute)
  }, AUTO_REDIRECTION_DELAY)
})

onUnmounted(() => {
  if (autoRedirectionTimeout.value) {
    clearTimeout(autoRedirectionTimeout.value)
  }
})
</script>
